import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { stoQueries } from './queries/stoQueries'
import { normalizeStoSlugForPath } from 'router/stoPaths'

/**
 * The gateway answering that the project does not exist (406 for an unknown name, or 404) or an empty
 * answer — not a network error, a 5xx, or another 4xx (401/403/429…), which may pass and must not send
 * a visitor to the 404 page.
 */
export const isProjectNotFoundError = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } } | null)?.response?.status
  if (status !== undefined) return status === 404 || status === 406
  return error instanceof Error && error.message.startsWith('Project not found')
}

/**
 * The page's root dependency. Reads the slug from the route, so sections can depend on the
 * project without the page threading it down; another route passes its own. The cache dedupes.
 */
export function useStoProject(slugOverride?: string) {
  const { stoSlug = '' } = useParams<{ stoSlug?: string }>()
  // A passed name is a project name, not a URL slug: names may hold spaces, so it is not normalised.
  const slug = slugOverride ?? normalizeStoSlugForPath(stoSlug)
  const query = useQuery(stoQueries.project(slug))

  return {
    slug,
    project: query.data,
    projectId: query.data?._id,
    isPending: query.isPending,
    isError: query.isError,
    isNotFound: query.isError && isProjectNotFoundError(query.error),
  }
}
